import { type LogAlarmResponse, type LogItemResponse } from '@common/types/api';
import { type DateTimeUTC, type GUID } from '@common/types/brandedTypes';
import { parseTimestampAsUtc } from '@common/utils/datetime/isoTimestamp';

export enum LogEventSeverity {
  All = 'All',
  None = 'None',
  Message = 'Message',
  Information = 'Information',
  Warning = 'Warning',
  Error = 'Error',
  Fatal = 'Fatal',
}

export const LogLevel = {
  None: -10,
  Message: 0,
  Information: 10,
  Warning: 20,
  Error: 30,
  Fatal: 40,
} as const;

export interface DeviceAlert {
  readonly id: number;
  readonly isActiveAlarm: boolean;
  readonly name: string;
  readonly description: string;
  readonly referenceId: number;
  readonly errorCode: number;
}

interface DeviceBackup {
  readonly backupId: GUID;
  readonly backupRestoreId?: GUID;
}

interface DeviceLogEvent {
  readonly id: number;
  readonly timestampUtc: DateTimeUTC;
  readonly title: string;
  readonly message: string;
  readonly user?: {
    id: string;
    lastName: string;
    firstName: string;
  };
  readonly severity: LogEventSeverity;
  readonly type: string;
  readonly alert?: DeviceAlert;
  readonly backup?: DeviceBackup;
}

const createDeviceAlertFromResponse = (
  alarmEvent?: LogAlarmResponse,
): DeviceAlert | undefined => {
  if (alarmEvent) {
    return {
      id: alarmEvent.id,
      isActiveAlarm: alarmEvent.isActive,
      name: alarmEvent.name,
      description: alarmEvent.description,
      referenceId: alarmEvent.registerIndex,
      errorCode: alarmEvent.code,
    };
  }
  return undefined;
};

export const createDeviceLogEventFromResponse = (
  apiLogEvent: LogItemResponse,
): DeviceLogEvent => {
  let user;
  if (apiLogEvent.user) {
    user = {
      id: apiLogEvent.user.id || apiLogEvent.userId,
      firstName: apiLogEvent.user.firstName,
      lastName: apiLogEvent.user.lastName,
    };
  }
  return {
    user,
    id: apiLogEvent.id,
    timestampUtc: parseTimestampAsUtc(apiLogEvent.timestampUtc),
    title: apiLogEvent.title,
    message: apiLogEvent.message,
    severity: apiLogEvent.severity as LogEventSeverity,
    type: apiLogEvent.type,
    alert: createDeviceAlertFromResponse(apiLogEvent.alarm),
    backup: apiLogEvent.backup,
  };
};

export default DeviceLogEvent;
